(async (algolia) => {
  if (!customElements.get('algolia-collection')) {
    // Wait for AlgoliaSharedUtils to be available
    const waitForSharedUtils = () => {
      return new Promise((resolve) => {
        if (window.AlgoliaSharedUtils) {
          resolve(window.AlgoliaSharedUtils);
        } else {
          setTimeout(() => resolve(waitForSharedUtils()), 50);
        }
      });
    };

    const AlgoliaSharedUtils = await waitForSharedUtils();

    // Wait for FacetManager to be available
    const waitForFacetManager = () => {
      return new Promise((resolve) => {
        if (window.FacetManager) {
          resolve(window.FacetManager);
        } else {
          setTimeout(() => resolve(waitForFacetManager()), 50);
        }
      });
    };

    const FacetManager = await waitForFacetManager();

    // Wait for WidgetFactory to be available
    const waitForWidgetFactory = () => {
      return new Promise((resolve) => {
        if (window.WidgetFactory) {
          resolve(window.WidgetFactory);
        } else {
          setTimeout(() => resolve(waitForWidgetFactory()), 50);
        }
      });
    };

    const WidgetFactory = await waitForWidgetFactory();

    // Wait for InstantSearch to be available from CDN
    const instantsearch = await AlgoliaSharedUtils.waitForInstantSearch();

    // Import collection component functions
    const { productCardHtml, emptyResultsHtml, generateProductBadges, initializeSwymData } = await import('./modules/algolia-collection-component.js');
    class AlgoliaCollection extends HTMLElement {
      constructor() {
        super();

        // Initialize Swym objects if they don't exist
        if (typeof window !== 'undefined') {
          const swymObjects = ['SwymViewProducts', 'SwymWatchProducts', 'SwymProductVariants'];
          for (const obj of swymObjects) {
            if (!window[obj]) window[obj] = {};
          }
        }
        try {
        // Initialize default values first
        this.colorMappings = [];
        this.lightColorMappings = '';
        this.colorFilterName = '';
        this.enableCollapsibleFilters = true;
        this.numberOfFiltersVisible = 5;
        this.maxNumberOfFilters = 50;
        this.openFilters = [];
        this.options = null;
        this.collectionHandle = null;
        this.wishlistItems = null;

        // Initialize FacetManager and WidgetFactory
        this.facetManager = null;
        this.widgetFactory = null;

        // Capture search query immediately on page load, before InstantSearch can clear it
        this.capturedSearchQuery = this.captureSearchQueryOnLoad();

        // Add page visibility listener to clear stored query when navigating away
        this.addPageVisibilityListener();

        // Check if algolia is available
        if (!algolia || !algolia.config) {
          this.showError('Algolia configuration not found');
          return;
        }
          this.initializeWithAlgolia();

        } catch (error) {
          // Don't break page loading, just schedule retry
          this.scheduleInitialization();
        }
      }

      initializeWithAlgolia() {
        try {
          // Parse data attributes safely
          this.collection = this.dataset?.collection || 'all';
          this.productsPerPage = parseInt(this.dataset?.productsPerPage) || 24;

          this.categoryFilter = this.dataset?.categoryFilter || '';
          this.categoryFilterName = this.dataset?.categoryFilterName || '';

          this.enableCollapsibleFilters = this.dataset?.enableCollapsibleFilters === 'true';

          // Parse color mappings before initializing FacetManager
          this.colorMappings = AlgoliaSharedUtils.parseColorMappings(this.dataset?.colorMappings);
          this.lightColorMappings = this.dataset?.lightColorMappings || '';
          this.colorFilterName = this.dataset?.colorFilterName || '';

          // Initialize FacetManager first
          if (typeof FacetManager === 'undefined') {
            throw new Error('FacetManager class is not available. Make sure algolia-facet-manager.js is loaded before algolia-collection.js');
          }
          this.facetManager = new FacetManager(algolia, {
            categoryFilter: this.categoryFilter,
            categoryFilterName: this.categoryFilterName,
            openFilters: [],
            colorMappings: this.colorMappings,
            lightColorMappings: this.lightColorMappings,
            enableResultCount: this.enableResultCount,
            enableCollapsibleFilters: this.enableCollapsibleFilters
          });

          // Initialize WidgetFactory
          if (typeof WidgetFactory === 'undefined') {
            throw new Error('WidgetFactory class is not available. Make sure algolia-widget-factory.js is loaded before algolia-collection.js');
          }
          this.widgetFactory = new WidgetFactory(this);

          // Parse open filters using FacetManager
          this.openFilters = this.facetManager.parseOpenFilters(this.dataset?.openFilters);

          // Update FacetManager with parsed open filters
          this.facetManager.updateOpenFilters(this.openFilters);
          this.numberOfFiltersVisible = parseInt(this.dataset?.numberOfFiltersVisible) || 5;
          this.maxNumberOfFilters = parseInt(this.dataset?.maxNumberOfFilters) || 50;

          this.categoryScrollbar = this.dataset?.categoryScrollbar === 'true';
          this.categorySwiperNavMobile = this.dataset?.categorySwiperNavMobile === 'true';
          this.categorySwiperNavDesktop = this.dataset?.categorySwiperNavDesktop === 'true';


        // Configuration options
        this.options = {
          indexName: `${algolia.config.index_prefix}products`,
          hitsPerPage: this.productsPerPage || 24,
          collection: this.collection || null,
          distinct: true,
          filters: {},
          sortByOptions: this.generateSortByOptions(algolia.config),
        };

        // Get collection handle from URL or data attribute
        this.collectionHandle = this.facetManager.getCollectionHandle();
        this.scheduleInitialization();

        } catch (error) {
          AlgoliaSharedUtils.logError('Error in initializeWithAlgolia:', error);
          this.showError('Failed to initialize Algolia: ' + error.message);
        }
      }

      generateSortByOptions(config) {
        // Start with relevance as the default first option
        const sortOptions = [
          { label: 'Relevance', value: `${config.index_prefix}products` }
        ];

        // Process sort_orders from config
        if (config.sort_orders && Array.isArray(config.sort_orders)) {
          config.sort_orders.forEach(sortOrder => {
            // Handle ascending order
            if (sortOrder.asc && sortOrder.asc.active && sortOrder.asc.title) {
              const value = `${config.index_prefix}products_${sortOrder.key}_asc`;
              sortOptions.push({
                label: sortOrder.asc.title,
                value: value
              });
            }

            // Handle descending order
            if (sortOrder.desc && sortOrder.desc.active && sortOrder.desc.title) {
              const value = `${config.index_prefix}products_${sortOrder.key}_desc`;
              sortOptions.push({
                label: sortOrder.desc.title,
                value: value
              });
            }
          });
        }

        return sortOptions;
      }

      scheduleInitialization() {
        // Initialize InstantSearch with a delay to ensure DOM is ready
        setTimeout(() => {
                    try {
          if (!this.isConnected) {
              setTimeout(() => this.safeInitInstantSearch(), 1000);
              return;
             }

             this.safeInitInstantSearch();
           } catch (error) {
             // Initialization failed
           }
        }, 100);
      }

      safeInitInstantSearch() {
        try {
          // Check if algolia is available now if it wasn't before
          if (!algolia || !algolia.config) {
            this.showError('Algolia configuration not found');
            return;
          }

          // Initialize with algolia if not done yet
          if (!this.options) {
            this.initializeWithAlgolia();
          }

          // Check if options are properly initialized
          if (!this.options || !this.options.indexName) {
            AlgoliaSharedUtils.logError('Options not properly initialized:', {
              options: this.options,
              indexName: this.options?.indexName,
              algoliaConfig: algolia?.config
            });
            this.showError('Algolia options not properly initialized');
            return;
          }

          // Check if required containers exist
          const requiredContainers = ['#hits-list', '#pagination', '#search-stats'];
          const missingContainers = requiredContainers.filter(selector => !document.querySelector(selector));

          if (missingContainers.length > 0) {
            return;
          }

          this.initInstantSearch();

        } catch (error) {
          this.showError('Failed to initialize search: ' + error.message);
        }
      }

      showError(message) {
        const hitsContainer = this.querySelector('#hits-list');
        if (hitsContainer) {
          hitsContainer.innerHTML = `
            <div class="error-message">
              <h3>Error</h3>
              <p>${message}</p>
            </div>
          `;
        }
      }

      captureSearchQueryOnLoad() {
        // First try to capture from URL if on search page (this takes priority)
        if (this.isSearchPage()) {
          const urlParams = new URLSearchParams(window.location.search);
          const query = urlParams.get('q');

          if (query) {
            // Store it for future use
            localStorage.setItem('algolia_search_query', query);
            return query;
          }
        }

        // Fallback: try to get from localStorage (set by previous page or external script)
        const storedQuery = localStorage.getItem('algolia_search_query');

        if (storedQuery) {
          return storedQuery;
        }

        return '';
      }

      getSearchQuery() {
        // First check if we're on a search page and get the query parameter from URL
        if (this.isSearchPage()) {
          const urlParams = new URLSearchParams(window.location.search);
          const query = urlParams.get('q');
          if (query) {
            return query;
          }
        }

        // Fallback to captured query
        if (this.capturedSearchQuery) {
          return this.capturedSearchQuery;
        }

        return '';
      }

      storeSearchQuery(query) {
        // Store search query in localStorage for persistence across page refreshes
        if (query && query.trim()) {
          localStorage.setItem('algolia_search_query', query.trim());
        } else {
          localStorage.removeItem('algolia_search_query');
        }
      }

      clearStoredSearchQuery() {
        // Clear the stored search query (useful when navigating away from search)
        localStorage.removeItem('algolia_search_query');
      }

      addPageVisibilityListener() {
        // Clear stored search query when user navigates away from search pages
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'hidden' && !this.isSearchPage()) {
            this.clearStoredSearchQuery();
          }
        });

        // Also clear when navigating away from search pages
        window.addEventListener('beforeunload', () => {
          if (!this.isSearchPage()) {
            this.clearStoredSearchQuery();
          }
        });
      }

      isSearchPage() {
        // Check if we're on a search results page
        const isSearch = window.location.pathname === '/search' || window.location.pathname.includes('/search');
        return isSearch;
      }


      updateSearchQueryDisplay() {
        // Only update on search pages
        if (!this.isSearchPage()) return;

        const searchPageTitle = document.querySelector('#search-page-title');
        if (!searchPageTitle) {
          return;
        }

        // Check if collection and helper are available
        if (!this.collection || !this.collection.helper) {
          return;
        }

        const currentQuery = this.collection.helper.state.query;

        if (currentQuery && currentQuery.trim()) {
          searchPageTitle.textContent = `Search results for "${currentQuery}"`;
        } else {
          // Use the no-query title from data attribute, fallback to default
          const noQueryTitle = searchPageTitle.getAttribute('data-algolia-search-title-no-query') ||
                               searchPageTitle.getAttribute('data-algolia-search-title') ||
                               'Search results';
          searchPageTitle.textContent = noQueryTitle;
        }
      }

      syncSearchInputValues() {
        // Only sync on search pages
        if (!this.isSearchPage()) return;

        // Check if collection and helper are available
        if (!this.collection || !this.collection.helper) return;

        const currentQuery = this.collection.helper.state.query;
        if (!currentQuery) return;

        // Store the current query in localStorage for persistence
        this.storeSearchQuery(currentQuery);

        // Update all search inputs with the current query
        this.syncAllSearchInputs(currentQuery);
      }

      syncAllSearchInputs(value) {
        // Update all search inputs with the provided value (except InstantSearch input)
        const searchInputs = document.querySelectorAll('#algoliaAutocompleteInput, #HeaderDesktopSearchInModal, [data-search-input]');
        searchInputs.forEach(input => {
          if (input.value !== value) {
            input.value = value;
          }
        });

        // Update the search results header if we're on a search page
        if (this.isSearchPage()) {
          this.updateSearchQueryDisplay();
        }

        // Store the query in localStorage for persistence
        if (value && value !== '' && value.trim()) {
          this.storeSearchQuery(value);
        } else {
          this.clearStoredSearchQuery();
        }
      }


      initInstantSearch() {
        if (!algolia || !algolia.searchClient) {
          return;
        }

        // Ensure options are properly initialized
        if (!this.options || !this.options.indexName) {
          this.showError('Algolia options not properly initialized');
          return;
        }

        const searchClient = algolia.searchClient;
        const filters = this.buildCollectionFilter();

        try {
          // Check if there's already a shared InstantSearch instance
          if (window.sharedAlgoliaSearch) {
            this.collection = window.sharedAlgoliaSearch;
          } else {
            // Capture indexName for use in stateMapping
            const indexName = this.options.indexName;

            // Create custom router to sync filter parameters with URL
            const router = instantsearch.routers.history({
              createURL({ qsModule, routeState, location }) {
                const { protocol, hostname, port = '', pathname, hash } = location;
                const queryString = qsModule.stringify(routeState);
                const portPart = port ? `:${port}` : '';
                return `${protocol}//${hostname}${portPart}${pathname}${queryString ? `?${queryString}` : ''}${hash}`;
              },
              parseURL({ qsModule, location }) {
                return qsModule.parse(location.search.slice(1));
              },
            });

            // Create a new shared InstantSearch instance with routing enabled
            this.collection = instantsearch({
              indexName: indexName,
              searchClient,
              routing: {
                router,
                stateMapping: {
                  stateToRoute(uiState) {
                    const indexUiState = uiState[indexName] || {};
                    return {
                      q: indexUiState.query || '',
                      page: indexUiState.page !== undefined ? indexUiState.page + 1 : undefined,
                      // Convert refinements to URL parameters
                      // Format: attribute:value1,value2 or attribute:value for single values
                      ...Object.keys(indexUiState.refinementList || {}).reduce((acc, attribute) => {
                        const values = indexUiState.refinementList[attribute];
                        if (values && values.length > 0) {
                          // Use attribute name as key, multiple values as comma-separated
                          acc[`filter.${attribute}`] = values.length === 1 ? values[0] : values.join(',');
                        }
                        return acc;
                      }, {}),
                      // Handle menu refinements
                      ...Object.keys(indexUiState.menu || {}).reduce((acc, attribute) => {
                        const value = indexUiState.menu[attribute];
                        if (value) {
                          acc[`filter.${attribute}`] = value;
                        }
                        return acc;
                      }, {}),
                      // Handle hierarchical menu refinements
                      ...Object.keys(indexUiState.hierarchicalMenu || {}).reduce((acc, attribute) => {
                        const value = indexUiState.hierarchicalMenu[attribute];
                        if (value) {
                          acc[`filter.${attribute}`] = value;
                        }
                        return acc;
                      }, {}),
                      // Handle range refinements
                      ...Object.keys(indexUiState.range || {}).reduce((acc, attribute) => {
                        const range = indexUiState.range[attribute];
                        if (range && (range.min !== undefined || range.max !== undefined)) {
                          if (range.min !== undefined && range.max !== undefined) {
                            acc[`filter.${attribute}`] = `${range.min}:${range.max}`;
                          } else if (range.min !== undefined) {
                            acc[`filter.${attribute}.min`] = range.min;
                          } else if (range.max !== undefined) {
                            acc[`filter.${attribute}.max`] = range.max;
                          }
                        }
                        return acc;
                      }, {}),
                      // Handle toggle refinements
                      ...Object.keys(indexUiState.toggle || {}).reduce((acc, attribute) => {
                        const value = indexUiState.toggle[attribute];
                        if (value) {
                          acc[`filter.${attribute}`] = value;
                        }
                        return acc;
                      }, {}),
                      // Handle sortBy
                      sortBy: indexUiState.sortBy || undefined,
                    };
                  },
                  routeToState(routeState) {
                    const refinementList = {};
                    const menu = {};
                    const hierarchicalMenu = {};
                    const range = {};
                    const toggle = {};

                    // Parse filter parameters
                    Object.keys(routeState).forEach(key => {
                      // Handle range min/max parameters first (before general filter. check)
                      if (key.startsWith('filter.') && key.endsWith('.min')) {
                        const attribute = key.replace('filter.', '').replace('.min', '');
                        range[attribute] = { ...range[attribute], min: parseFloat(routeState[key]) };
                      } else if (key.startsWith('filter.') && key.endsWith('.max')) {
                        const attribute = key.replace('filter.', '').replace('.max', '');
                        range[attribute] = { ...range[attribute], max: parseFloat(routeState[key]) };
                      } else if (key.startsWith('filter.')) {
                        const attribute = key.replace('filter.', '');
                        const value = routeState[key];

                        // Determine refinement type based on value format
                        if (value.includes(':')) {
                          // Range refinement (min:max or min: or :max)
                          const [min, max] = value.split(':');
                          range[attribute] = {
                            ...(min && min !== '' ? { min: parseFloat(min) } : {}),
                            ...(max && max !== '' ? { max: parseFloat(max) } : {}),
                          };
                        } else if (value.includes(',')) {
                          // Multiple values (refinementList)
                          refinementList[attribute] = value.split(',').map(v => v.trim());
                        } else if (value.includes(' > ')) {
                          // Hierarchical menu
                          hierarchicalMenu[attribute] = value;
                        } else if (value === 'true' || value === 'false') {
                          // Toggle refinement
                          toggle[attribute] = value === 'true';
                        } else {
                          // Single value - try refinementList first, then menu
                          // We'll default to refinementList for single values
                          refinementList[attribute] = [value];
                        }
                      }
                    });

                    return {
                      [indexName]: {
                        query: routeState.q || '',
                        page: routeState.page ? parseInt(routeState.page) - 1 : undefined,
                        refinementList: Object.keys(refinementList).length > 0 ? refinementList : undefined,
                        menu: Object.keys(menu).length > 0 ? menu : undefined,
                        hierarchicalMenu: Object.keys(hierarchicalMenu).length > 0 ? hierarchicalMenu : undefined,
                        range: Object.keys(range).length > 0 ? range : undefined,
                        toggle: Object.keys(toggle).length > 0 ? toggle : undefined,
                        sortBy: routeState.sortBy || undefined,
                      },
                    };
                  },
                },
              },
            });

            // Store the shared instance globally
            window.sharedAlgoliaSearch = this.collection;
          }

        } catch (error) {
          this.showError('Failed to create InstantSearch instance: ' + error.message);
          return;
        }

        // Configure InstantSearch with facet discovery
        const initialQuery = this.isSearchPage() ? this.capturedSearchQuery : '';

        // Read page parameter from URL (1-indexed) and convert to 0-indexed for InstantSearch
        const urlParams = new URLSearchParams(window.location.search);
        const urlPage = urlParams.get('page');
        const initialPage = urlPage ? Math.max(0, parseInt(urlPage) - 1) : 0;

        const configureWidget = instantsearch.widgets.configure({
          hitsPerPage: this.options.hitsPerPage,
          distinct: true, // Re-enabled for product deduplication
          filters: filters || undefined,
          query: initialQuery,
          page: initialPage, // Set initial page from URL
          facets: ['*'],
          maxValuesPerFacet: 200,
          attributesToRetrieve: ['*'],
          // Add facet-specific configuration for better counts
          facetFilters: filters ? [filters] : undefined,
        });

        this.collection.addWidgets([configureWidget]);

        // Ensure search query is preserved immediately after configuration
        this.ensureSearchQueryPreservation();

        // Add search box widget (only on search pages)
        this.addSearchBoxWidget();
        // Add current filters widget
        this.addCurrentFiltersWidget();
        // Add filter widgets dynamically
        this.addDynamicFilterWidgets();
        // Add category filter slider
        this.addCategoryFilterSlider();

        // Add hits widget
        this.addHitsWidget();
        // Add pagination widget
        this.addPaginationWidget();
        // Add sort by widget
        this.addSortByWidget();
        // Add stats widget
        this.addStatsWidget();
        // Add total results update widget
        this.addTotalResultsUpdateWidget();

        // Add search event listeners for better timing
        this.collection.on('render', () => {
          setTimeout(() => {
            this.checkAndOpenDefaultCollapsibles();
            this.facetManager.handleDisabledShowMoreButtons();

            // Update slider navigation state after facets are loaded
            this.updateSliderNavigationState();

            // Dispatch custom event for Swym to re-initialize buttons
            document.dispatchEvent(new CustomEvent("swym:collections-loaded"));

            // Only call search page methods if we're on a search page and collection is ready
            if (this.isSearchPage() && this.collection && this.collection.helper) {
              try {
                this.updateSearchQueryDisplay();
                this.syncSearchInputValues();
              } catch (error) {
                AlgoliaSharedUtils.logError('Error updating search display:', error);
              }
            }
          }, 100);
        });

        // Add search event listeners
        this.collection.on('search:error', (event) => {
          AlgoliaSharedUtils.logError('Algolia search error:', event);
        });

        // Store search results for use in filter count updates
        this.collection.on('render', () => {
          // Get results from the helper state after render
          if (this.collection.helper && this.collection.helper.lastResults) {
            this.collection.lastResults = this.collection.helper.lastResults;
          }
        });


        // Start the search if it hasn't been started yet
        if (!window.sharedAlgoliaSearchStarted) {
          try {
            this.collection.start();
            window.sharedAlgoliaSearchStarted = true;

            // Routing will handle URL sync automatically, including initial state from URL
            // Trigger an initial search to get facets
            setTimeout(() => {
              this.collection.helper.search();
              // Sync search input values after initial search
              try {
                this.syncSearchInputValues();
              } catch (error) {
                AlgoliaSharedUtils.logError('Error syncing search input values:', error);
              }
            }, 100);

          } catch (error) {
            this.showError('Failed to start search: ' + error.message);
            return;
          }
        }

        // Add event listeners
        this.addEventListeners();
      }

      buildCollectionFilter() {
        let filters = [];

        // Don't apply collection filters on search pages - we want to search all products
        if (this.isSearchPage()) {
          return '';
        }

        // Add collection filter if we have a collection handle
        if (this.collectionHandle) {
          filters.push(`collections:${this.collectionHandle}`);
        }

        // Additional filters can be added here, e.g., based on user selections
        const filterString = filters.length > 0 ? filters.join(' AND ') : '';
        return filterString;
      }

      initializeClearButtonVisibility() {
        // Initially hide the clear button since no filters are active on page load
        const clearButton = this.querySelector('[data-clear-refinements]');
        if (clearButton) {
          clearButton.style.display = 'none';
        }
      }

      updateClearButtonVisibility() {
        // Backup method to ensure clear button visibility is always correct
        if (!this.collection || !this.collection.helper) return;

        // Check if there are any active refinements
        const hasActiveRefinements = this.collection.helper.state &&
          Object.keys(this.collection.helper.state.facetsRefinements || {}).length > 0;

        // Count total refinements
        const totalRefinements = hasActiveRefinements ?
          Object.values(this.collection.helper.state.facetsRefinements || {}).reduce((total, refinements) => {
            return total + (Array.isArray(refinements) ? refinements.length : 0);
          }, 0) : 0;

        // Get total results from the last search results if available
        const totalResults = this.collection.lastResults ? this.collection.lastResults.nbHits : null;

        // Update the active filter count (data-total-refined-values)
        AlgoliaSharedUtils.updateActiveFilterCount(totalRefinements, totalResults);
      }


      addCurrentFiltersWidget() {
        try {
          // Create container for current filters
          const filtersContainer = this.querySelector('.filter-options__main');
          if (!filtersContainer) {
            return;
          }

          // Create the current filters container
          const currentFiltersContainer = document.createElement('div');
          currentFiltersContainer.className = 'filter-option';
          currentFiltersContainer.id = 'current-filters-container';
          currentFiltersContainer.style.display = 'none'; // Hidden by default

          // Check if current filters should be open by default
          const shouldBeOpenByDefault = this.facetManager.shouldFacetBeOpenByDefault('Current filters');

          currentFiltersContainer.innerHTML = this.facetManager.createFilterContainerHTML('Current filters', 'current-filters', {
            shouldBeOpenByDefault
          });

          // Insert at the beginning of the filters container
          filtersContainer.insertBefore(currentFiltersContainer, filtersContainer.firstChild);

          // Create custom currentRefinements widget using connectCurrentRefinements
          const formatAttributeName = this.facetManager.formatAttributeName.bind(this.facetManager);
          const enableCollapsibleFilters = this.enableCollapsibleFilters;
          const querySelector = this.querySelector.bind(this);

          // Create the render function for current refinements
          const createDataAttributes = (refinement, originalAttribute) => {
            const attributes = {
              'attribute': originalAttribute,
              'type': 'disjunctive',
              'value': refinement.value || refinement.label,
              'label': refinement.label,
              'count': refinement.count || '',
              'exhaustive': 'true'
            };

            return Object.keys(attributes)
              .map(key => `data-${key}="${attributes[key]}"`)
              .join(' ');
          };

          const renderRefinementItem = (refinement, originalAttribute) => {
            // Find the facet configuration to get the same display name used in filter facets
            const enabledFacets = this.facetManager.getCollectionFacets();
            const facetConfig = enabledFacets.find(facet =>
              (facet.attribute || facet.name) === originalAttribute
            );

            // Use the same logic as filter facets: displayName || title || formatAttributeName
            const displayName = facetConfig ?
              (facetConfig.displayName || facetConfig.title || this.facetManager.formatAttributeName(originalAttribute)) :
              this.facetManager.formatAttributeName(originalAttribute);

            return `
              <a ${createDataAttributes(refinement, originalAttribute)}
                 class="active-filter-options__button is-flex is-aligned-center ais-current-refined-values--link">
                <svg class="icon icon-close icon--sm" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <title>Close</title>
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M6.34338 6.34315C6.7339 5.95262 7.36707 5.95262 7.75759 6.34315L12.0002 10.5858L16.2429 6.34315C16.6334 5.95262 17.2666 5.95262 17.6571 6.34315C18.0476 6.73367 18.0476 7.36683 17.6571 7.75736L13.4144 12L17.6571 16.2426C18.0476 16.6332 18.0476 17.2663 17.6571 17.6569C17.2666 18.0474 16.6334 18.0474 16.2429 17.6569L12.0002 13.4142L7.75759 17.6569C7.36707 18.0474 6.7339 18.0474 6.34338 17.6569C5.95285 17.2663 5.95285 16.6332 6.34338 16.2426L10.586 12L6.34338 7.75736C5.95285 7.36684 5.95285 6.73367 6.34338 6.34315Z" fill="currentColor"></path>
                </svg>
                <span class="ais-current-refined-values--label">
                  ${displayName}:
                </span>
                ${refinement.label}
              </a>
            `;
          };

          const renderCurrentRefinements = (renderOptions, isFirstRender) => {
            const { items, refine, widgetParams, results } = renderOptions;
            const container = document.querySelector('#current-filters');
            const mainContainer = document.getElementById('current-filters-container');
            const clearButton = querySelector('[data-clear-refinements]');

            if (!container) return;

            // Calculate total refinements
            const totalRefinements = items.reduce((total, item) => total + item.refinements.length, 0);

            // Get total results count from the search results - try multiple sources
            let totalResults = null;
            if (results && results.nbHits !== undefined) {
              totalResults = results.nbHits;
            } else if (this.collection && this.collection.lastResults && this.collection.lastResults.nbHits !== undefined) {
              totalResults = this.collection.lastResults.nbHits;
            }

            // Update the active filter count (data-total-refined-values)
            AlgoliaSharedUtils.updateActiveFilterCount(totalRefinements, totalResults);

            if (totalRefinements > 0) {
              // Show main container when there are active filters
              if (mainContainer) {
                mainContainer.style.display = 'block';
              }

              // Render the refinements
              container.innerHTML = `
                <ul class="current-filters__list list-unstyled">
                  ${items.map(item => `
                    <li class="active-filter-options__item">
                      ${item.refinements.map(refinement =>
                        renderRefinementItem(refinement, item.attribute)
                      ).join('')}
                    </li>
                  `).join('')}
                </ul>
              `;

              // Auto-expand the collapsible only if collapsibles are enabled
              if (enableCollapsibleFilters && mainContainer) {
                const collapsible = mainContainer.querySelector('[data-collapsible]');
                const content = mainContainer.querySelector('[data-collapsible-content]');

                if (collapsible && content) {
                  collapsible.classList.add('collapsible-is-open');
                  setTimeout(() => {
                    if (content.scrollHeight > 0) {
                      const hasShowMoreButtons = content.querySelector('.filter-options__show-more');
                      if (hasShowMoreButtons) {
                        content.style.height = 'auto';
                        content.setAttribute('data-has-show-more', 'true');
                      } else {
                        content.style.height = content.scrollHeight + 'px';
                      }
                    }
                  }, 100);
                }
              }

              // Add click event listeners to remove refinements
              container.querySelectorAll('.ais-current-refined-values--link').forEach(element => {
                element.addEventListener('click', (event) => {
                  event.preventDefault();
                  const dataset = event.currentTarget.dataset;
                  const refinementToRemove = {
                    attribute: dataset.attribute,
                    type: dataset.type,
                    value: dataset.value,
                    label: dataset.label
                  };
                  refine(refinementToRemove);
                });
              });

            } else {
              // Hide containers when no active filters
              container.innerHTML = '';
            }
          };

          // Create the custom widget using connectCurrentRefinements
          const customCurrentRefinements = instantsearch.connectors.connectCurrentRefinements(
            renderCurrentRefinements
          );

          const currentRefinementsWidget = customCurrentRefinements({
            container: document.querySelector('#current-filters')
          });
          this.collection.addWidgets([currentRefinementsWidget]);

         } catch (error) {
           // Error adding current filters widget
           AlgoliaSharedUtils.logError('Error adding current filters widget:', error);
         }
      }

      addDynamicFilterWidgets() {
        // Early check for InstantSearch instance
        if (!this.collection) {
          return;
        }

        // Check if algolia config is available
        if (!algolia || !algolia.config) {
          this.addSimpleCategoryFilter();
          return;
        }

        try {
          // Get collection-specific facets or fallback to default facets
          // This now returns only enabled, non-hidden facets
          let facetsToUse = this.facetManager.getCollectionFacets();

          if (!facetsToUse || facetsToUse.length === 0) {
            this.addSimpleCategoryFilter();
            return;
          }

          // Create containers and widgets for facets
          this.createConfiguredFacetContainers(facetsToUse);
          this.addConfiguredFacetWidgets(facetsToUse);

          this.facetsCreated = true;

        } catch (error) {
          AlgoliaSharedUtils.logError('Error in addDynamicFilterWidgets:', error);
          this.addSimpleCategoryFilter();
        }
      }

      createConfiguredFacetContainers(enabledFacets) {
        const filtersContainer = this.querySelector('.filter-options__main');
        if (!filtersContainer) {
          return;
        }

        enabledFacets.forEach((facet) => {
          const attribute = facet.attribute || facet.name;
          const displayName = facet.displayName || facet.title || this.facetManager.formatAttributeName(attribute);
          const containerId = `facet-${attribute.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`;

          // Check if container already exists
          if (document.querySelector(`#${containerId}`)) {
            return;
          }

          const containerElement = document.createElement('div');
          containerElement.className = 'filter-option';
          containerElement.id = `${containerId}-wrapper`;

          // Check if this facet should be open by default
          const shouldBeOpenByDefault = this.facetManager.shouldFacetBeOpenByDefault(displayName);

          // Use the createFilterContainerHTML helper for consistent structure
          containerElement.innerHTML = this.facetManager.createFilterContainerHTML(displayName, containerId, {
            shouldBeOpenByDefault
          });

          filtersContainer.appendChild(containerElement);
        });
      }

      addConfiguredFacetWidgets(enabledFacets) {
        try {
          const widgets = this.widgetFactory.createFacetWidgets(enabledFacets);
          this.collection.addWidgets(widgets);
        } catch (error) {
          AlgoliaSharedUtils.logError('Error adding configured facet widgets:', error);
        }
      }

      addSimpleCategoryFilter() {
        // Prevent multiple calls
        if (this.simpleCategoryFilterAdded) {
          return;
        }

        // Check if InstantSearch instance exists
        if (!this.collection) {
          AlgoliaSharedUtils.logError('InstantSearch instance (this.collection) is undefined in addSimpleCategoryFilter');
          return;
        }

        this.simpleCategoryFilterAdded = true;

        try {
          const filtersContainer = this.querySelector('.filter-options__main');
          if (filtersContainer && !document.querySelector('#simple-category-filter')) {
            const containerElement = document.createElement('div');
            containerElement.className = 'filter-option';
            containerElement.id = 'simple-category-wrapper';

            containerElement.innerHTML = `
              <div class="filter-options__details">
                <h3 class="label-medium weight-medium filter-options__title">Collections</h3>
                <div class="facet-content" id="simple-category-filter"></div>
              </div>
            `;

            filtersContainer.appendChild(containerElement);
          }

          // Add simple refinement list for collections using WidgetFactory
          const simpleCategoryWidget = this.widgetFactory.createSimpleCategoryFilter();
          this.collection.addWidgets([simpleCategoryWidget]);

          // Mark facets as created to prevent infinite loop
          this.facetsCreated = true;
        } catch (error) {
          AlgoliaSharedUtils.logError('Error adding simple category filter:', error);
          // Even if there's an error, mark as created to prevent infinite loop
          this.facetsCreated = true;
        }
      }

      checkAndOpenDefaultCollapsibles() {
        // Only run if we haven't marked this as completed
        if (this.hasAttribute('data-default-collapsibles-opened')) {
          return;
        }

        // Find all collapsibles that should be open by default but aren't open yet
        const defaultOpenCollapsibles = this.querySelectorAll('[data-collapsible][data-open-by-default="true"]:not(.collapsible-is-open)');

        if (defaultOpenCollapsibles.length > 0) {
          this.openDefaultCollapsibles();
        }
      }

      openDefaultCollapsibles() {
        // Find all collapsibles that should be open by default
        const defaultOpenCollapsibles = this.querySelectorAll('[data-collapsible][data-open-by-default="true"]');

        if (defaultOpenCollapsibles.length === 0) {
          return;
        }

        let openedCount = 0;

        defaultOpenCollapsibles.forEach((collapsible, index) => {
          // Skip if already open
          if (collapsible.classList.contains('collapsible-is-open')) {
            return;
          }

          const content = collapsible.querySelector('[data-collapsible-content]');
          const title = collapsible.querySelector('.label-medium')?.textContent?.trim();
          const trigger = collapsible.querySelector('[data-collapsible-trigger]');

          // Check if content has actual content (facet items)
          const facetItems = content?.querySelectorAll('.ais-RefinementList-item, .ais-Menu-item');

          if (content && facetItems && facetItems.length > 0) {
            // Add open class
            collapsible.classList.add('collapsible-is-open');

            // Set height for proper expansion
            setTimeout(() => {
              if (content.scrollHeight > 0) {
                // Check if this collapsible contains facets with show more functionality
                const hasShowMoreButtons = content.querySelector('.filter-options__show-more');
                if (hasShowMoreButtons) {
                  // Set to auto for expandable content
                  content.style.height = 'auto';
                  content.setAttribute('data-has-show-more', 'true');
                } else {
                  // Normal fixed height for non-expandable content
                  content.style.height = content.scrollHeight + 'px';
                }

                openedCount++;
              }
            }, 50);
          }
        });

        // Mark as completed after opening some collapsibles
        if (openedCount > 0) {
          setTimeout(() => {
            this.setAttribute('data-default-collapsibles-opened', 'true');
          }, 500);
        }
      }

      addHitsWidget() {
        const hitsContainer = this.querySelector('#hits-list');

        if (hitsContainer) {
          try {
            this.collection.addWidgets([
              instantsearch.widgets.hits({
                container: '#hits-list',
                cssClasses: {
                  list: 'collection__grid is-grid list-unstyled',
                  item: 'is-col-6 is-col-4-md'
                },
                transformItems: (items) => {
                  return items.map(item => {
                    // Split title on single quote
                    const titleParts = item.title
                      ? (() => {
                          const idx = item.title.indexOf("'");
                          return idx !== -1
                            ? [item.title.slice(0, idx), item.title.slice(idx + 1)]
                            : ['', item.title];
                        })()
                      : ['', ''];
                      // If title ends with double single quotes '', replace with a single quote '
                      if (typeof titleParts[1] === 'string' && titleParts[1].endsWith("''")) {
                        titleParts[1] = titleParts[1].slice(0, -1);
                      }

                    // Get raw prices for comparison
                    const rawPrice = item.price;
                    const rawCompareAtPrice = item.compare_at_price || item.variants?.[0]?.compare_at_price;

                    // Determine if product is on sale using raw prices
                    const isOnSale = rawCompareAtPrice && rawCompareAtPrice > 0 && rawCompareAtPrice > rawPrice;

                    // Format prices for display
                    const price = AlgoliaSharedUtils.formatPrice(rawPrice, {}, this);
                    const compareAtPrice = isOnSale ? AlgoliaSharedUtils.formatPrice(rawCompareAtPrice, {}, this) : null;

                    // Add formatted prices to the item for use in templates
                    item.formattedPrice = price;
                    item.formattedCompareAtPrice = compareAtPrice;

                    // Generate product badges using the modular component
                    const badges = generateProductBadges(item, isOnSale);

                    // Construct product URL exactly like original snippet
                    const productUrl = `${window.location.origin}${item.url || item.product_url || (item.handle ? `/products/${item.handle}` : '#')}`;

                    // Get variants - use first variant as current variant
                    const variants = item.variants || [{ id: item.objectID, price: item.price, inventory_quantity: 0, available: true, title: 'Default' }];
                    const currentVariant = variants[0];

                    // Use algoliaShopify helpers if available, otherwise fallback
                    let wishlistState = '';
                    let wishlistImage = item.image;
                    let fullTitle = item.title;

                    try {
                      const helpers = window.algoliaShopify?.helpers;
                      if (helpers) {
                        wishlistState = helpers.wishlistState?.bind(item)() || '';
                        wishlistImage = helpers.wishlistImage?.bind(item)() || item.image;
                        fullTitle = helpers.fullTitle?.bind(item)() || item.title;
                      }
                    } catch (error) {
                      AlgoliaSharedUtils.logError('Error using algoliaShopify helpers:', error);
                    }

                    // Ensure we have a valid title
                    fullTitle = fullTitle?.trim() || item.title || 'Unknown Product';

                    // Initialize Swym data for this product
                    initializeSwymData(item, productUrl, item.image || '/assets/no-image.gif');

                    return {
                      ...item,
                      titleFirstPart: titleParts[0] ? titleParts[0].trim() : '',
                      titleSecondPart: titleParts[1] ? titleParts[1].trim() : item.title || '',
                      price: price,
                      compare_at_price: compareAtPrice,
                      isOnSale: isOnSale,
                      productUrl: productUrl,
                      badges: badges,
                      wishlistState: wishlistState,
                      wishlistImage: wishlistImage,
                      fullTitle: fullTitle,
                      variants: variants,
                      swymData: {
                        empi: item.objectID,
                        epi: currentVariant.id,
                        dt: fullTitle,
                        du: productUrl,
                        ct: item.product_type || '',
                        pr: parseFloat(price),
                        stk: currentVariant.inventory_quantity || 0,
                        iu: wishlistImage,
                        variants: [{ [currentVariant.title || 'Default']: currentVariant.id }]
                      }
                    };
                  });
                },
                templates: {
                  item: (item, { html }) => {
                    try {
                      // Use the modular component to render the product card
                      return productCardHtml(item, { element: this });
                    } catch (error) {
                      AlgoliaSharedUtils.logError('Error rendering product card:', error);
                      // Fallback to basic HTML
                      return `
                        <div class="ais-hit ais-product product-card product-card--default" data-product-id="${item.objectID}">
                          <div class="product-card__inner">
                            <div class="product-card__image">
                              <a href="${item.url || '#'}" class="full-unstyled-link product-card__image--link" onclick="window.AA && window.AA.clickedOnProduct(${item.objectID})">
                                <img src="${item.image || '/assets/no-image.gif'}" alt="${item.title}" width="100%" />
                              </a>
                              ${item.badges}
                            </div>
                            <a href="${item.url || '#'}" class="product-card__information full-unstyled-link" onclick="window.AA && window.AA.clickedOnProduct(${item.objectID})">
                              <span class="product-card__title h5 weight-regular">${item.title}</span>
                              <div class="product-card__price">
                              <span class="price-item">${item.formattedPrice || AlgoliaSharedUtils.formatPrice(item.price || 0, {}, this)}</span>
                              </div>
                            </a>
                          </div>
                        </div>
                      `;
                    }
                  },
                  empty: (options, { html }) => {
                    try {
                      return emptyResultsHtml();
                    } catch (error) {
                      AlgoliaSharedUtils.logError('Error rendering empty results:', error);
                      return '<div class="no-results"><h5>No products found</h5></div>';
                    }
                  },
                },
              }),
            ]);
          } catch (error) {
            AlgoliaSharedUtils.logError('Error adding hits widget:', error);
          }
        } else {
          AlgoliaSharedUtils.logError('Hits container #hits-list not found in DOM');
        }
      }

      addPaginationWidget() {
        const paginationContainer = this.querySelector('#pagination');
        if (!paginationContainer) {
          AlgoliaSharedUtils.logError('Pagination container #pagination not found in DOM');
          return;
        }

        // Clear any existing content (in case default pagination was rendered)
        paginationContainer.innerHTML = '';

        // Render function for custom pagination
        const renderPagination = (renderOptions, isFirstRender) => {
          try {
            const {
              pages,
              currentRefinement,
              nbPages,
              isFirstPage,
              isLastPage,
              canRefine,
              refine,
              createURL
            } = renderOptions;

            // Don't render if can't refine
            if (!canRefine) {
              paginationContainer.innerHTML = '';
              return;
            }

            // Use routing's createURL function which handles all URL parameters automatically
            const createPageURL = (page) => {
              // Create URL with updated page state using routing
              return createURL({ page });
            };

          paginationContainer.innerHTML = `
            <ul class="pagination__list list-unstyled">
              ${
                !isFirstPage
                  ? `
                    <li class="ais-Pagination-item pagination-item pagination__first">
                      <a
                        href="${createPageURL(0)}"
                        class="pagination__item label-medium weight-medium"
                        data-value="0"
                      >
                        «
                      </a>
                    </li>
                    <li class="ais-Pagination-item pagination-item pagination__prev">
                      <a
                        href="${createPageURL(currentRefinement - 1)}"
                        class="pagination__item label-medium weight-medium"
                        data-value="${currentRefinement - 1}"
                      >
                        ‹
                      </a>
                    </li>
                    `
                  : ""
              }
              ${pages
                .map(
                  (page) => {
                    const isCurrent = currentRefinement === page;
                    const currentClass = isCurrent ? ' pagination__current' : '';
                    return `
                      <li class="ais-Pagination-item pagination-item ais-Pagination-item--page${currentClass}">
                        <a
                          href="${createPageURL(page)}"
                          class="pagination__item label-medium weight-medium"
                          data-value="${page}"
                          aria-label="${page + 1}"
                        >
                          ${page + 1}
                        </a>
                      </li>
                    `;
                  }
                )
                .join("")}
              ${
                !isLastPage
                  ? `
                    <li class="ais-Pagination-item pagination-item pagination__next">
                      <a
                        href="${createPageURL(currentRefinement + 1)}"
                        class="pagination__item label-medium weight-medium"
                        data-value="${currentRefinement + 1}"
                      >
                        ›
                      </a>
                    </li>
                    <li class="ais-Pagination-item pagination-item pagination__last">
                      <a
                        href="${createPageURL(nbPages - 1)}"
                        class="pagination__item label-medium weight-medium"
                        data-value="${nbPages - 1}"
                      >
                        »
                      </a>
                    </li>
                    `
                  : ""
              }
            </ul>
          `;

          // Add click event listeners
          [...paginationContainer.querySelectorAll("a")].forEach((element) => {
            element.addEventListener("click", (event) => {
              event.preventDefault();
              const page = parseInt(event.currentTarget.dataset.value);
              if (!isNaN(page)) {
                // Update InstantSearch state - routing will handle URL updates automatically
                refine(page);

                // Scroll to top of the page
                window.scrollTo({ top: 0, behavior: 'instant' });
              }
            });
          });
          } catch (error) {
            AlgoliaSharedUtils.logError('Error rendering pagination:', error);
            // Fallback: render empty or show error
            paginationContainer.innerHTML = '';
          }
        };

        // Create custom pagination widget using connectPagination
        // Access connectors from window.instantsearch (like WidgetFactory does)
        if (!window.instantsearch || !window.instantsearch.connectors || !window.instantsearch.connectors.connectPagination) {
          AlgoliaSharedUtils.logError('connectPagination connector not available', {
            instantsearch: window.instantsearch,
            connectors: window.instantsearch?.connectors,
            availableConnectors: window.instantsearch?.connectors ? Object.keys(window.instantsearch.connectors) : []
          });
          return;
        }

        const connectPagination = window.instantsearch.connectors.connectPagination;
        const customPagination = connectPagination(renderPagination);

        // Add the custom pagination widget
        try {
          this.collection.addWidgets([
            customPagination({
              container: paginationContainer
            })
          ]);
        } catch (error) {
          AlgoliaSharedUtils.logError('Error adding custom pagination widget:', error);
        }
      }

      addSortByWidget() {
        const sortContainer = this.querySelector('#sort-by');
        if (sortContainer) {
          // Get the sort by label from data attribute, fallback to "Sort by"
          const sortByLabel = this.dataset.sortByLabel || 'Sort by';

          // Create the custom sort structure
          sortContainer.innerHTML = `
            <div class="sort-options">
              <button class="sort-options__button button button--gray button--md" aria-label="${sortByLabel}">
                <svg class="icon icon-sort icon--md" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="presentation" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M11.9,9.3L8.5,5.9c-0.3-0.3-0.8-0.3-1.1,0c0,0,0,0,0,0L4,9.3c-0.3,0.3-0.3,0.8,0,1.1c0.3,0.3,0.8,0.3,1.1,0
                    c0,0,0,0,0,0l2.2-2.2v9.4c0,0.4,0.3,0.8,0.8,0.8c0.4,0,0.8-0.3,0.8-0.8V8.2l2.2,2.2c0.3,0.3,0.8,0.3,1.1,0S12.2,9.6,11.9,9.3
                    L11.9,9.3z M20,13.6c-0.3-0.3-0.8-0.3-1.1,0c0,0,0,0,0,0l-2.2,2.2V6.4c0-0.4-0.3-0.8-0.8-0.8S15.3,6,15.3,6.4v9.4l-2.2-2.2
                    c-0.3-0.3-0.8-0.3-1.1,0s-0.3,0.8,0,1.1l3.4,3.4c0.3,0.3,0.8,0.3,1.1,0c0,0,0,0,0,0l3.4-3.4C20.3,14.4,20.3,13.9,20,13.6
                    C20,13.6,20,13.6,20,13.6z"></path>
                </svg>
                <span class="visibility-hidden visibility-md--visible">${sortByLabel}</span>
                <svg class="icon icon-collapsible icon--sm" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M5.29289 8.29289C5.68342 7.90237 6.31658 7.90237 6.70711 8.29289L12 13.5858L17.2929 8.29289C17.6834 7.90237 18.3166 7.90237 18.7071 8.29289C19.0976 8.68342 19.0976 9.31658 18.7071 9.70711L12.7071 15.7071C12.3166 16.0976 11.6834 16.0976 11.2929 15.7071L5.29289 9.70711C4.90237 9.31658 4.90237 8.68342 5.29289 8.29289Z" fill="currentColor"></path>
                </svg>
              </button>
              <span class="ais-sort-orders-container" id="sort-by-select-container"></span>
            </div>
          `;

          // Add the InstantSearch sortBy widget to the select container
          this.collection.addWidgets([
            instantsearch.widgets.sortBy({
              container: '#sort-by-select-container',
              items: this.options.sortByOptions,
              cssClasses: {
                root: 'ais-SortBy',
                select: 'ais-SortBy-select sort-options__select'
              },
            }),
          ]);

          // Add click handler for the sort button to toggle visibility
          const sortButton = sortContainer.querySelector('.sort-options__button');
          const sortSelectContainer = sortContainer.querySelector('.ais-sort-orders-container');

          if (sortButton && sortSelectContainer) {
            // Initially hide the select container

            sortButton.addEventListener('click', (e) => {
              e.preventDefault();
              const isVisible = sortSelectContainer.style.display !== 'none';
              sortSelectContainer.style.display = isVisible ? 'none' : 'block';

              // Toggle collapsible icon rotation
              const collapsibleIcon = sortButton.querySelector('.icon-collapsible');
              if (collapsibleIcon) {
                collapsibleIcon.style.transform = isVisible ? 'rotate(0deg)' : 'rotate(180deg)';
              }
            });
          }
        } else {
          AlgoliaSharedUtils.logError('Sort container #sort-by not found in DOM');
        }
      }

      addCategoryFilterSlider() {
        const categoryFilterContainer = this.querySelector('#category-filter');
        if (!categoryFilterContainer) {
          return;
        }

        // Use the category filter attribute from data attributes, fallback to default
        const categoryFilterAttribute = this.categoryFilter || 'named_tags.key-container';

        // Only create the slider if a category filter is specified
        if (!categoryFilterAttribute || categoryFilterAttribute.trim() === '') {
          return;
        }

        // Determine slider wrapper class based on scrollbar setting
        const sliderWrapperClass = this.categoryScrollbar ? 'slider__wrapper slider__wrapper--scroll-bar' : 'slider__wrapper slider__wrapper--no-scroll-bar';

        // Determine navigation button visibility classes
        const mobileNavClass = this.categorySwiperNavMobile ? '' : 'visibility-hidden';
        const desktopNavClass = this.categorySwiperNavDesktop ? 'visibility-md--visible' : 'visibility-md--hidden';
        const navVisibilityClass = `${mobileNavClass} ${desktopNavClass}`;

        // Create the slider component structure
        categoryFilterContainer.innerHTML = `
          <slider-component id="CollectionFiltersCategory" class="featured-collection-section__slider slider hide-start-gradient category-options" data-filter-category-slider data-options='{
            "offset": 16,
            "mobileOffset": 16,
            "gradient": true
          }'>
            <div class="slider__container category-options__inner">
              <div class="${sliderWrapperClass}" data-scroller-wrapper style="cursor: grab;">
                <div class="slider__content" data-scroller-content id="category-filter-facet">
                </div>
              </div>
            </div>
            <div class="slider__buttons">
              <div class="slider__buttons-inner">
                <button data-slider-prev class="slider__prev-button slider__prev-button--disabled ${navVisibilityClass}" aria-label="Slide left">
                  <svg class="icon icon-caret icon--xs" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="presentation" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M5.52876 3.52861C5.26841 3.78896 5.26841 4.21107 5.52876 4.47141L9.05735 8.00001L5.52876 11.5286C5.26841 11.789 5.26841 12.2111 5.52876 12.4714C5.78911 12.7318 6.21122 12.7318 6.47157 12.4714L10.4716 8.47141C10.7319 8.21106 10.7319 7.78895 10.4716 7.52861L6.47157 3.52861C6.21122 3.26826 5.78911 3.26826 5.52876 3.52861Z" fill="currentColor"></path>
                  </svg>
                </button>
                <button data-slider-next class="slider__next-button ${navVisibilityClass}" aria-label="Slide right">
                  <svg class="icon icon-caret icon--xs" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="presentation" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M5.52876 3.52861C5.26841 3.78896 5.26841 4.21107 5.52876 4.47141L9.05735 8.00001L5.52876 11.5286C5.26841 11.789 5.26841 12.2111 5.52876 12.4714C5.78911 12.7318 6.21122 12.7318 6.47157 12.4714L10.4716 8.47141C10.7319 8.21106 10.7319 7.78895 10.4716 7.52861L6.47157 3.52861C6.21122 3.26826 5.78911 3.26826 5.52876 3.52861Z" fill="currentColor"></path>
                  </svg>
                </button>
              </div>
            </div>
          </slider-component>
        `;

        // Add the refinement list widget for the category filter using WidgetFactory
        const categorySliderWidget = this.widgetFactory.createCategoryFilterSlider();
        this.collection.addWidgets([categorySliderWidget]);

        // Initialize the slider component if available
        setTimeout(() => {
          const sliderComponent = categoryFilterContainer.querySelector('slider-component');
          if (sliderComponent && window.SliderComponent) {
            // Check if slider component is already initialized
            if (sliderComponent._sliderInitialized) {
              this.updateSliderNavigationState();
              this.initializeActiveOptionObserver();
              return;
            }

            // Initialize the slider component
            const sliderInstance = new window.SliderComponent(sliderComponent);
            sliderComponent._sliderInitialized = true;

            // Update navigation button states after initialization
            setTimeout(() => {
              this.updateSliderNavigationState();
              // Initialize active option observer after slider is fully initialized
              setTimeout(() => {
                this.initializeActiveOptionObserver();
              }, 100);

              // Ensure slider navigation works by manually calling the slider methods
              const prevButton = sliderComponent.querySelector('[data-slider-prev]');
              const nextButton = sliderComponent.querySelector('[data-slider-next]');
              const scrollerWrapper = sliderComponent.querySelector('[data-scroller-wrapper]');
              const scrollerContent = sliderComponent.querySelector('[data-scroller-content]');

              if (prevButton) {
                prevButton.addEventListener('click', (e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  // Check if slider is properly initialized
                  if (sliderInstance && sliderInstance.selectors && sliderInstance.selectors.wrapper) {
                    sliderInstance.goToNextPrevSlide('backward');
                  } else {
                    // Fallback: manually scroll the wrapper
                    if (scrollerWrapper) {
                      const currentScroll = scrollerWrapper.scrollLeft;
                      const scrollAmount = scrollerWrapper.clientWidth * 0.75;
                      scrollerWrapper.scrollTo({
                        left: Math.max(0, currentScroll - scrollAmount),
                        behavior: 'smooth'
                      });

                      // Update button states after scrolling
                      setTimeout(() => {
                        this.updateSliderNavigationState();
                      }, 300);
                    }
                  }
                });
              }

              if (nextButton) {
                nextButton.addEventListener('click', (e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  // Check if slider is properly initialized
                  if (sliderInstance && sliderInstance.selectors && sliderInstance.selectors.wrapper) {
                    sliderInstance.goToNextPrevSlide('forward');
                  } else {
                    // Fallback: manually scroll the wrapper
                    if (scrollerWrapper && scrollerContent) {
                      const currentScroll = scrollerWrapper.scrollLeft;
                      const scrollAmount = scrollerWrapper.clientWidth * 0.75;
                      const maxScroll = scrollerContent.scrollWidth - scrollerWrapper.clientWidth;
                      scrollerWrapper.scrollTo({
                        left: Math.min(maxScroll, currentScroll + scrollAmount),
                        behavior: 'smooth'
                      });

                      // Update button states after scrolling
                      setTimeout(() => {
                        this.updateSliderNavigationState();
                      }, 300);
                    }
                  }
                });
              }
            }, 200);
          }
        }, 100);
      }

      updateSliderNavigationState() {
        const sliderComponent = this.querySelector('slider-component');
        if (!sliderComponent) return;

        const prevButton = sliderComponent.querySelector('[data-slider-prev]');
        const nextButton = sliderComponent.querySelector('[data-slider-next]');
        const scrollerWrapper = sliderComponent.querySelector('[data-scroller-wrapper]');
        const scrollerContent = sliderComponent.querySelector('[data-scroller-content]');

        if (!prevButton || !nextButton || !scrollerWrapper || !scrollerContent) return;

        // Check if slider can scroll
        const canScrollLeft = scrollerWrapper.scrollLeft > 0;
        const canScrollRight = scrollerWrapper.scrollLeft < (scrollerContent.scrollWidth - scrollerWrapper.clientWidth);

        // Update button states
        if (canScrollLeft) {
          prevButton.classList.remove('slider__prev-button--disabled');
        } else {
          prevButton.classList.add('slider__prev-button--disabled');
        }

        if (canScrollRight) {
          nextButton.classList.remove('slider__next-button--disabled');
        } else {
          nextButton.classList.add('slider__next-button--disabled');
        }
      }

      initializeActiveOptionObserver() {
        const sliderComponent = this.querySelector('slider-component');
        if (!sliderComponent) return;

        const scrollerWrapper = sliderComponent.querySelector('[data-scroller-wrapper]');
        const scrollerContent = sliderComponent.querySelector('[data-scroller-content]');

        if (!scrollerWrapper || !scrollerContent) return;

        // Use a more direct approach - listen for clicks on filter options
        const categoryFilterFacet = document.querySelector('#category-filter-facet');
        if (categoryFilterFacet) {
          // Add click listener to handle active option scrolling
          categoryFilterFacet.addEventListener('click', (event) => {
            const clickedOption = event.target.closest('.category-options__option');
            if (clickedOption) {
              // Small delay to allow the active class to be applied
              setTimeout(() => {
                if (clickedOption.classList.contains('category-options__option--active')) {
                  this.scrollToActiveOption(clickedOption, scrollerWrapper);
                }
              }, 100);
            }
          });
        }
      }

      scrollToActiveOption(activeElement, scrollerWrapper) {
        if (!activeElement || !scrollerWrapper) return;

        // Get the position of the active element relative to the scroller content
        const scrollerContent = scrollerWrapper.querySelector('[data-scroller-content]');
        if (!scrollerContent) return;

        const activeElementRect = activeElement.getBoundingClientRect();
        const scrollerRect = scrollerWrapper.getBoundingClientRect();
        const scrollerContentRect = scrollerContent.getBoundingClientRect();

        // Calculate the current scroll position
        const currentScrollLeft = scrollerWrapper.scrollLeft;
        const scrollerWidth = scrollerWrapper.clientWidth;

        // Calculate the position of the active element within the scroller content
        const elementLeft = activeElementRect.left - scrollerContentRect.left;
        const elementRight = elementLeft + activeElementRect.width;

        // Calculate the desired scroll position to center the active element
        const elementCenter = elementLeft + (activeElementRect.width / 2);
        const scrollerCenter = scrollerWidth / 2;
        const targetScrollLeft = currentScrollLeft + (elementCenter - scrollerCenter);

        // Ensure we don't scroll beyond the bounds
        const maxScrollLeft = scrollerContent.scrollWidth - scrollerWidth;
        const finalScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));

        // Perform smooth scroll to the calculated position
        scrollerWrapper.scrollTo({
          left: finalScrollLeft,
          behavior: 'smooth'
        });

        // Update navigation button states after scrolling
        setTimeout(() => {
          this.updateSliderNavigationState();
        }, 300);
      }

      ensureSearchQueryPreservation() {
        // Only run on search pages
        if (!this.isSearchPage()) return;

        if (this.capturedSearchQuery) {
          // Ensure the search query is in the URL immediately
          const url = new URL(window.location);
          if (!url.searchParams.has('q') || url.searchParams.get('q') !== this.capturedSearchQuery) {
            url.searchParams.set('q', this.capturedSearchQuery);
            window.history.replaceState(null, '', url);
          }

          // Also ensure it's set in the InstantSearch helper if available
          if (this.collection && this.collection.helper) {
            this.collection.helper.setQuery(this.capturedSearchQuery);
          }
        }
      }

      addSearchBoxWidget() {
        // Only add this widget on search pages
        if (!this.isSearchPage()) return;

        const searchBoxContainer = this.querySelector('.ais-search-box-container');
        if (!searchBoxContainer) return;

        try {
          this.collection.addWidgets([
            instantsearch.widgets.searchBox({
              container: '.ais-search-box-container',
              placeholder: 'Search for products',
              showReset: true,
              showSubmit: true,
              showLoadingIndicator: true,
              cssClasses: {
                root: 'ais-SearchBox',
                form: 'ais-SearchBox-form',
                input: 'ais-SearchBox-input search__input field__input',
                submit: 'ais-SearchBox-submit',
                reset: 'ais-SearchBox-reset',
                loadingIndicator: 'ais-SearchBox-loadingIndicator',
                submitIcon: 'ais-SearchBox-submitIcon',
                resetIcon: 'ais-SearchBox-resetIcon',
                loadingIcon: 'ais-SearchBox-loadingIcon'
              },
              templates: {
                submit: `
                  <svg class="ais-SearchBox-submitIcon" xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 40 40">
                    <path d="M26.804 29.01c-2.832 2.34-6.465 3.746-10.426 3.746C7.333 32.756 0 25.424 0 16.378 0 7.333 7.333 0 16.378 0c9.046 0 16.378 7.333 16.378 16.378 0 3.96-1.406 7.594-3.746 10.426l10.534 10.534c.607.607.61 1.59-.004 2.202-.61.61-1.597.61-2.202.004L26.804 29.01zm-10.426.627c7.323 0 13.26-5.936 13.26-13.26 0-7.32-5.937-13.257-13.26-13.257C9.056 3.12 3.12 9.056 3.12 16.378c0 7.323 5.936 13.26 13.258 13.26z"></path>
                  </svg>
                `,
                reset: 'Clear',
                loadingIndicator: `
                  <svg class="ais-SearchBox-loadingIcon" width="16" height="16" viewBox="0 0 38 38" xmlns="http://www.w3.org/2000/svg" stroke="#444">
                    <g fill="none" fillrule="evenodd">
                      <g transform="translate(1 1)" strokewidth="2">
                        <circle strokeopacity=".5" cx="18" cy="18" r="18"></circle>
                        <path d="M36 18c0-9.94-8.06-18-18-18">
                          <animateTransform attributeName="transform" type="rotate" from="0 18 18" to="360 18 18" dur="1s" repeatCount="indefinite"></animateTransform>
                        </path>
                      </g>
                    </g>
                  </svg>
                `
              }
            })
          ]);

          // Add custom event listeners to handle clear button properly
          this.addSearchBoxEventListeners();
        } catch (error) {
          AlgoliaSharedUtils.logError('Error adding search box widget:', error);
        }
      }

      addSearchBoxEventListeners() {
        // Wait for the search box to be rendered
        setTimeout(() => {
          const searchInput = this.querySelector('.ais-SearchBox-input');
          const resetButton = this.querySelector('.ais-SearchBox-reset');

          if (!searchInput || !resetButton) return;

          // Function to update clear button visibility
          const updateClearButtonVisibility = () => {
            const hasValue = searchInput.value && searchInput.value !== '';
            if (hasValue) {
              resetButton.classList.remove('hidden')
            } else {
              resetButton.classList.add('hidden')
            }
          };

          // Initial visibility check
          updateClearButtonVisibility();

          // Add click event listener to the reset button
          resetButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            // Clear the captured search query
            this.capturedSearchQuery = '';
            this.clearStoredSearchQuery();

            // Clear InstantSearch's internal state
            if (this.collection && this.collection.helper) {
              this.collection.helper.setQuery('').search();
            }

            // Clear the input value directly
            searchInput.value = '';

            // Clear URL parameter if present
            const url = new URL(window.location);
            if (url.searchParams.has('q')) {
              url.searchParams.delete('q');
              window.history.replaceState(null, '', url);
            }

            // Sync with all other search inputs
            this.syncAllSearchInputs('');

            // Update clear button visibility
            updateClearButtonVisibility();

            // Force a re-render to ensure the state is properly cleared
            setTimeout(() => {
              if (this.collection && this.collection.helper) {
                this.collection.helper.search();
              }
            }, 50);
          });

          // Add focus event listener to prevent state restoration
          searchInput.addEventListener('focus', (event) => {
            // If the input is empty but InstantSearch has a query, clear it
            if (!searchInput.value && this.collection && this.collection.helper) {
              const currentQuery = this.collection.helper.state.query;
              if (currentQuery && currentQuery.trim()) {
                // Clear InstantSearch's internal state
                this.collection.helper.setQuery('').search();
                // Ensure input stays empty
                searchInput.value = '';
                // Update clear button visibility
                updateClearButtonVisibility();
              }
            }
          });

          // Add input event listener to keep state in sync and update clear button visibility
          searchInput.addEventListener('input', (event) => {
            const currentValue = event.target.value;

            // Update captured query
            this.capturedSearchQuery = currentValue;

            // Update URL parameter
            const url = new URL(window.location);
            if (currentValue.trim()) {
              url.searchParams.set('q', currentValue);
            } else {
              url.searchParams.delete('q');
            }
            window.history.replaceState(null, '', url);

            // Sync with other search inputs
            this.syncAllSearchInputs(currentValue);

            // Update clear button visibility
            updateClearButtonVisibility();
          });

          // Also listen for InstantSearch state changes to update clear button visibility
          if (this.collection) {
            this.collection.on('render', () => {
              // Small delay to ensure the input value is updated
              setTimeout(() => {
                updateClearButtonVisibility();
              }, 10);
            });
          }
        }, 100);
      }

      addStatsWidget() {
        const statsContainer = this.querySelector('#search-stats');
        if (statsContainer) {
          this.collection.addWidgets([
            instantsearch.widgets.stats({
              container: '#search-stats',
              templates: {
                text: `
                  {{#hasNoResults}}No results{{/hasNoResults}}
                  {{#hasOneResult}}1 result{{/hasOneResult}}
                  {{#hasManyResults}}{{#helpers.formatNumber}}{{nbHits}}{{/helpers.formatNumber}} results{{/hasManyResults}}
                `,
              },
            }),
          ]);
        } else {
          AlgoliaSharedUtils.logError('Stats container #search-stats not found in DOM');
        }
      }

      addTotalResultsUpdateWidget() {
        // Create a custom widget that updates data-total-result with the same results as stats
        const totalResultsUpdateWidget = {
          render: ({ results, state }) => {
            if (results) {
              // Update the data-total-result element with the same results as stats widget
              AlgoliaSharedUtils.updateTotalResultsOnly(results.nbHits);
            }
          },
          init: ({ results, state }) => {
            if (results) {
              AlgoliaSharedUtils.updateTotalResultsOnly(results.nbHits);
            }
          }
        };

        this.collection.addWidgets([totalResultsUpdateWidget]);
      }

      addEventListeners() {
        // Add clear button listener
        this.querySelector('[data-clear-refinements]')?.addEventListener('click', () => this.clearRefinements());

        // Initialize wishlist functionality
        this.initializeWishlistButtons();

        // Trigger Swym instrumentation after a delay to ensure all products are processed
        setTimeout(() => {
          try {
            window._swat?.instrument?.();
          } catch (instrumentError) {
            AlgoliaSharedUtils.logError('Error triggering Swym instrumentation:', instrumentError);
          }
        }, 1000);

        // Initialize collapsible elements after facets are created
        setTimeout(() => {
          this.initializeCollapsibleFilters();
          this.injectShowMoreStyles();

          // Open default collapsibles based on data-open-filters - delay more to ensure facets are loaded
          setTimeout(() => {
            this.openDefaultCollapsibles();
            this.facetManager.handleDisabledShowMoreButtons();
          }, 500);

          // Also try again a bit later in case facets load slowly
          setTimeout(() => {
            this.openDefaultCollapsibles();
            this.facetManager.handleDisabledShowMoreButtons();
          }, 1500);
        }, 1000);

        this.addEventListener('click', (event) => {
          const { target } = event;

          // Handle add to cart
          if (target.matches('[data-add-to-cart]')) {
            event.preventDefault();
            const variantId = target.dataset.variantId;
            window.CartDrawer?.addItem?.(variantId, 1);
            return;
          }

          // Handle quickshop
          if (target.matches('[data-quickshop]')) {
            event.preventDefault();
            const handle = target.dataset.quickshop;
            window.QuickShop?.open?.(handle);
            return;
          }

          // Handle collapsible filter toggles
          const collapsibleTrigger = target.matches('[data-collapsible-trigger]')
            ? target
            : target.closest('[data-collapsible-trigger]');

          if (collapsibleTrigger) {
            event.preventDefault();
            this.toggleCollapsibleFilter(collapsibleTrigger);
            return;
          }

          // Handle show more/show less button clicks
          const showMoreButton = target.closest('.filter-options__show-more');
          if (showMoreButton) {
            this.facetManager.handleShowMoreClick(showMoreButton);
            // Update disabled button states after show more click
            setTimeout(() => this.facetManager.handleDisabledShowMoreButtons(), 100);
          }
        });

      }

      initializeCollapsibleFilters() {
        // Initialize toggle-more functionality if available
        window.ToggleMore && this.querySelectorAll('[data-collapsible]').forEach(container => {
          !container.hasAttribute('data-toggle-more-initialized') &&
            new window.ToggleMore(container) &&
            container.setAttribute('data-toggle-more-initialized', 'true');
        });
      }

      toggleCollapsibleFilter(trigger) {
        const collapsible = trigger.closest('[data-collapsible]');
        if (!collapsible) return;

        const content = collapsible.querySelector('[data-collapsible-content]');
        const isOpen = collapsible.classList.contains('collapsible-is-open');

        if (isOpen) {
          collapsible.classList.remove('collapsible-is-open');
          content.style.height = '0px';
        } else {
          collapsible.classList.add('collapsible-is-open');

          // Check if this collapsible contains facets with show more functionality
          const hasShowMoreButtons = content.querySelector('.filter-options__show-more');
          content.style.height = hasShowMoreButtons ? 'auto' : content.scrollHeight + 'px';
          hasShowMoreButtons && content.setAttribute('data-has-show-more', 'true');
        }
      }




      injectShowMoreStyles() {
        // Inject CSS styles for show more functionality
        if (document.getElementById('algolia-show-more-styles')) return; // Already injected

        const style = document.createElement('style');
        style.id = 'algolia-show-more-styles';

        document.head.appendChild(style);
      }

      initializeWishlistButtons() {
        // Initialize wishlist functionality
        this.initWishlist();

        // Set up Swym callback for wishlist events
        this.setupSwymCallback();

        // Event delegation to handle clicks on wishlist buttons (only add once)
        if (!window.wishlistClickHandlerAdded) {
          window.wishlistClickHandlerAdded = true;
          document.addEventListener('click', (event) => {
            // Check if the clicked element is a wishlist button or inside one
            const wishlistButton = event.target.closest('.wishlist-trigger');
            if (wishlistButton) {
              event.preventDefault();
              event.stopPropagation();

              // Get all the data attributes from the button
              const productData = {
                empi: wishlistButton.getAttribute('data-empi'),
                epi: wishlistButton.getAttribute('data-epi'),
                dt: wishlistButton.getAttribute('data-dt'),
                du: wishlistButton.getAttribute('data-du'),
                ct: wishlistButton.getAttribute('data-ct'),
                pr: wishlistButton.getAttribute('data-pr'),
                stk: wishlistButton.getAttribute('data-stk'),
                iu: wishlistButton.getAttribute('data-iu')
              };

              // Debug: Log the raw data from the button
              AlgoliaSharedUtils.logError('Raw product data from button:', productData);

              // Call the Swym API to add the product to the wishlist
              this.addToWishlist(productData);
            }
          });
        }
      }

      // Get wishlist items and apply "swym-added" class to existing items
      async initWishlist() {
        let countIntervals = 0;
        return await new Promise((resolve, reject) => {
          const interval = setInterval(() => {
            countIntervals++;

            // Check if Wishlist app is initialized
            if (window?._swat && window?._swat?.fetch) {
              clearInterval(interval);

              // Fetch wishlist items and resolve results
              window._swat.fetch((results) => {

                // Save results
                this.wishlistItems = results;

                // Apply results on page load
                for (let result in results) {
                  const item = results[result];
                  if (!item.empi) {
                    return;
                  }
                  document.querySelectorAll(`.ais-Hits .wishlist-trigger.product_${item.empi}`).forEach((button) => {
                    button.classList.add('swym-added');
                  });
                }

                resolve(results);
              });
            }

            // No wishlist app, throw error.
            if (countIntervals == 100) {
              clearInterval(interval);
              AlgoliaSharedUtils.logError('Swym wishlist app not found after 25 seconds');
              reject('No wishlist app');
            }
          }, 250);
        });
      }

      // Set up Swym callback for wishlist events
      setupSwymCallback() {
        // Prevent duplicate callback registration across all instances
        if (window.swymCallbackRegistered) {
          return;
        }
        window.swymCallbackRegistered = true;

        const swymCallbackFn = (swat) => {
          swat.evtLayer.addEventListener(swat.JSEvents.addedToWishlist, (event) => {
            var product = event?.detail?.d;
            if (!product) {
              return false;
            }

            // Update the button state to show it's been added
            const button = document.querySelector(`.wishlist-trigger.product_${product.empi}`);
            if (button) {
              button.classList.add('swym-added');
            }

            // Notification delay
            setTimeout(() => {
              // If swym-notifications-container swymTopLeft swym-showing
              if (document.querySelector('.swym-notifications-container.swym-showing')) {
                if (!document.querySelector('.swym-notifications-container.custom')) {
                  return true;
                }
              }

              // Else display a custom notification
              const notification = `
                <div class="swym-notifications-container custom swymTopLeft">
                  <a href="/pages/swym-wishlist" class="swym-notification-wishlist swym-clickable">
                    <div class="swym-product-tile">
                      <button class="swym-close-btn swym-is-button">
                        <svg class="swym-icon icon icon-close icon--xs" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="presentation" viewBox="0 0 24 24">
                          <title>Close</title>
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M18.1,17.1c0.3,0.3,0.3,0.8,0,1.1s-0.8,0.3-1.1,0L12,13.1l-5.1,5.1c-0.3,0.3-0.8,0.3-1.1,0
                          c-0.3-0.3-0.3-0.8,0-1.1l5.1-5.1L5.9,6.9c-0.3-0.3-0.3-0.8,0-1.1s0.8-0.3,1.1,0l5.1,5.1l5.1-5.1c0.3-0.3,0.8-0.3,1.1,0
                          c0.3,0.3,0.3,0.8,0,1.1l0,0L13.1,12L18.1,17.1z"></path>
                        </svg>
                      </button>
                      <img class="swym-image" src="${product.iu}" alt="">
                      <div class="swym-details-container">
                        <div class="swym-details" role="alert">
                          <strong>${product.dt}</strong> has been added to your wishlist successfully.
                        </div>
                      </div>
                    </div>
                  </a>
                </div>
              `;

              const container = document.querySelector('.swym-wishlist-notifications-container');

              container.innerHTML = notification;

              const popup = container.querySelector('.swym-notifications-container');

              // animate in.
              setTimeout(() => {
                if (popup) {
                  popup.classList.add('swym-showing');
                }
              }, 250);

              // animate out.
              setTimeout(() => {
                if (popup) {
                  popup.classList.remove('swym-showing');
                }
              }, 6000);

              // animate out on click close btn
              container.querySelector('.swym-notifications-container .swym-close-btn ').addEventListener('click', (e) => {
                e.preventDefault();
                container.querySelector('.swym-notifications-container').classList.remove('swym-showing');
              });
            }, 250);
          });
        };

        if (!window.SwymCallbacks) {
          window.SwymCallbacks = [];
        }
        window.SwymCallbacks.push(swymCallbackFn);
      }

      addToWishlist(product) {
        // Check if Swym is available
        if (!window._swat) {
          AlgoliaSharedUtils.logError('Swym _swat is not available');
          return;
        }

        // Check if addToWishList method exists
        if (!window._swat.addToWishList) {
          AlgoliaSharedUtils.logError('Swym addToWishList method is not available');
          return;
        }

        try {
          // Validate and fix product data with proper fallbacks
          const fixedProduct = {
            empi: parseInt(product.empi) || 0,
            epi: parseInt(product.epi) || 0,
            dt: product.dt || 'Unknown Product',
            du: product.du || window.location.origin,
            ct: product.ct || '',
            pr: parseFloat(product.pr) || 0,
            stk: parseInt(product.stk) || 0,
            iu: product.iu || '/assets/no-image.gif'
          };

          // Validate required fields
          if (!fixedProduct.empi || !fixedProduct.epi) {
            AlgoliaSharedUtils.logError('Invalid product data: missing empi or epi', product);
            return;
          }

          // Fix localhost URLs for Swym (replace with production domain)
          if (fixedProduct.du && (fixedProduct.du.includes('127.0.0.1:9292') || fixedProduct.du.includes('localhost'))) {
            fixedProduct.du = fixedProduct.du.replace(/http:\/\/127\.0\.0\.1:9292/g, 'https://farmer-gracy.myshopify.com');
            fixedProduct.du = fixedProduct.du.replace(/http:\/\/localhost:\d+/g, 'https://farmer-gracy.myshopify.com');
          }

          // Debug: Log the data being sent to Swym
          AlgoliaSharedUtils.logError('Sending to Swym addToWishList:', fixedProduct);

          // Use Swym API to add product to wishlist with callback function (like working implementation)
          window._swat.addToWishList(
            fixedProduct,
            function(result) {
              AlgoliaSharedUtils.logError('Successfully added to wishlist:', result);
              // Update button state if needed
              const button = document.querySelector(`.wishlist-trigger.product_${fixedProduct.empi}`);
              if (button) {
                button.classList.add('swym-added');
              }
            },
            false
          );
        } catch (error) {
          AlgoliaSharedUtils.logError('Error calling Swym addToWishList:', error);
        }
      }


      clearRefinements() {
        this.collection?.helper?.clearRefinements().search();
      }



    }

    window.AlgoliaCollection = AlgoliaCollection;
    customElements.define('algolia-collection', AlgoliaCollection);
  }
})(window.algoliaShopify);
